import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CtaSection from '@/components/feature/CtaSection';
import ContactForm from '@/components/feature/ContactForm';
import { services } from '@/mocks/services';
import { testimonials } from '@/mocks/testimonials';
import { company } from '@/mocks/company';

const heroImg = '/assets/klob-hero-main.jpg';
const aboutImg = '/assets/klob-about-02-green.jpg';
const contactBg = '/assets/klob-contact-bg.jpg';

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-50 font-sans">
      <Header />

      <main>
        {/* HERO */}
        <section className="relative min-h-screen w-full overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImg} alt="Handwerker beim Verputzen einer Wand" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/30"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/80 via-background-50/30 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-20 pt-32">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-50/40 px-4 py-1.5 text-[12px] uppercase tracking-[0.2em] text-primary-300 backdrop-blur mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                Bau- &amp; Ausbauhandwerk aus Hollfeld
              </span>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight text-foreground-50">
                Handwerk, das<br />Bestand hat.
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-foreground-100/85">
                Professionelle Putz-, Ausbau- und Handwerksarbeiten aus Hollfeld — sauber, zuverlässig und fachgerecht umgesetzt.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  Projekt anfragen
                </Link>
                <a
                  href={company.phoneHref}
                  className="inline-flex items-center justify-center h-12 px-8 rounded-md border border-foreground-50/30 bg-background-50/30 backdrop-blur hover:bg-foreground-50/10 text-foreground-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-phone-line mr-2"></i>{company.phone} anrufen
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-foreground-50/15 pt-6">
                <div className="flex items-center gap-2">
                  <span className="flex text-primary-400 gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-${i < 4 ? 'fill' : 'half-fill'} text-sm`}></i>
                    ))}
                  </span>
                  <span className="text-sm font-medium text-foreground-100/90">{company.rating} bei Google</span>
                </div>
                <span className="h-5 w-px bg-foreground-50/20"></span>
                <div className="flex items-center gap-3 text-sm font-medium text-foreground-100/80">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>Erfahrung
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>Qualität
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>Handwerk
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="leistungen" className="relative bg-background-50 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col gap-4">
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Leistungen</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Unser <span className="text-primary-500">Handwerk</span>
              </h2>
              <p className="max-w-2xl text-lg text-foreground-400">
                Vom Erstputz bis zum fertigen Innenausbau — wir übernehmen die komplette Umsetzung Ihrer Bau- und Ausbauprojekte.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-x-8 lg:gap-y-12">
              {services.map((s) => (
                <Link
                  key={s.num}
                  to="/leistungen"
                  className="group overflow-hidden rounded-sm border border-background-300/60 bg-background-100 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/50 cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute left-3 top-3 rounded bg-background-50/60 px-2.5 py-1 font-mono text-sm text-primary-300 backdrop-blur">{s.num}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-background-300/60 px-5 py-5">
                    <div>
                      <h3 className="text-xl font-bold text-foreground-50">{s.title}</h3>
                      <p className="mt-1 text-sm leading-snug text-foreground-400">{s.desc}</p>
                    </div>
                    <i className="ri-arrow-right-line text-2xl text-primary-400 ml-3 transition-transform group-hover:translate-x-1"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section id="ueber-uns" className="relative overflow-hidden border-y border-background-300/40 bg-background-100 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-sm border border-primary-500/40"></div>
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                <img src={aboutImg} alt="Handwerker bei der Arbeit" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 -right-3 sm:-right-6 rounded-sm bg-primary-500 px-6 py-5 text-center">
                <p className="font-display text-3xl font-bold text-background-50">{company.rating}</p>
                <p className="text-[11px] uppercase tracking-widest text-background-50/90">bei Google</p>
              </div>
            </div>
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Über uns</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Handwerk mit <span className="text-primary-500">Anspruch.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-foreground-100/75">
                Die {company.name} steht für saubere, zuverlässige und fachgerechte Ausführung. Wir legen Wert auf Qualität in
                jedem Arbeitsschritt — vom ersten Gespräch bis zur fertigen Oberfläche.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-foreground-100/75">
                Individuelle Betreuung und ein ehrlicher, bodenständiger Anspruch an unser Handwerk prägen jedes Projekt, das wir
                für Sie umsetzen.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { t: 'Präzise', s: 'Saubere Ausführung' },
                  { t: 'Zuverlässig', s: 'Termintreu & ehrlich' },
                  { t: 'Persönlich', s: 'Direkte Betreuung' },
                ].map((x) => (
                  <div key={x.t} className="rounded-sm border border-background-300/60 bg-background-50/30 px-4 py-4">
                    <p className="font-display text-2xl font-bold text-primary-300">{x.t}</p>
                    <p className="mt-1 text-sm text-foreground-100/65">{x.s}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/ueber-uns"
                className="mt-8 inline-flex items-center h-12 px-7 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                Mehr über uns <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section id="ablauf" className="bg-background-50 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Ablauf</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                In 3 Schritten zu Ihrem <span className="text-primary-500">Projekt</span>
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { n: '01', t: 'Anfrage stellen', d: 'Teilen Sie uns Ihr Vorhaben telefonisch oder online mit.' },
                { n: '02', t: 'Projekt besprechen', d: 'Wir klären Anforderungen und die passende Umsetzung.' },
                { n: '03', t: 'Umsetzung', d: 'Professionelle und saubere Ausführung Ihres Projekts.' },
              ].map((s) => (
                <div
                  key={s.n}
                  className="group relative rounded-sm border border-background-300/60 bg-background-100 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/50"
                >
                  <span className="font-display text-6xl font-bold text-primary-500/25">{s.n}</span>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-[2px] w-8 bg-primary-500"></span>
                    <span className="text-sm text-foreground-400">Schritt {s.n}</span>
                  </div>
                  <h3 className="mt-4 text-2xl font-bold text-foreground-50">{s.t}</h3>
                  <p className="mt-2 text-lg leading-relaxed text-foreground-400">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="referenzen" className="relative overflow-hidden bg-background-100 py-24 lg:py-32">
          <div className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-primary-500/10 blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col gap-4">
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Referenzen</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Bewertungen unserer <span className="text-primary-500">Kunden</span>
              </h2>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 rounded-sm border border-primary-500/30 bg-background-50/20 px-6 py-8">
              <p className="font-display text-7xl font-bold text-primary-300">{company.rating}</p>
              <div className="flex flex-col gap-1">
                <div className="flex text-primary-400 gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`ri-star-${i < 4 ? 'fill' : 'half-fill'} text-xl`}></i>
                  ))}
                </div>
                <p className="text-base text-foreground-100/80">
                  von 5 bei Google · {company.reviewCount} Rezensionen
                </p>
              </div>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <figure
                  key={i}
                  className="flex flex-col gap-4 rounded-sm border border-background-300/60 bg-background-50/40 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/40"
                >
                  <div className="flex text-primary-500 gap-0.5">
                    {[...Array(5)].map((_, k) => (
                      <i key={k} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-lg leading-relaxed text-foreground-100/85">„{t.text}"</blockquote>
                  <div className="mt-auto flex items-center gap-2 text-[13px] text-foreground-400">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    {t.source}
                  </div>
                </figure>
              ))}
            </div>
            <Link
              to="/referenzen"
              className="mt-10 inline-flex items-center h-12 px-7 rounded-md border border-primary-500/50 text-primary-300 hover:bg-primary-500 hover:text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              Alle Referenzen ansehen <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>

        <CtaSection />

        {/* CONTACT */}
        <section id="kontakt" className="relative bg-background-50 py-24 lg:py-32">
          <div className="absolute inset-0 opacity-10">
            <img src={contactBg} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 relative z-10">
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Kontakt</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Ihr Projekt — <span className="text-primary-500">unverbindlich anfragen</span>
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-foreground-400">
                Beschreiben Sie uns kurz Ihr Vorhaben. Wir melden uns zeitnah bei Ihnen und besprechen die passende Umsetzung.
              </p>
              <div className="mt-10 space-y-4 rounded-sm border border-background-300/60 bg-background-100 p-6">
                <a
                  href={company.phoneHref}
                  className="group flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4 transition-colors hover:border-primary-500/50 hover:bg-primary-500/5 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">Telefon</p>
                    <p className="text-sm font-semibold text-foreground-50">{company.phone}</p>
                  </div>
                </a>
                <a
                  href={`mailto:${company.email}`}
                  className="group flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4 transition-colors hover:border-primary-500/50 hover:bg-primary-500/5 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">E-Mail</p>
                    <p className="text-sm font-semibold text-foreground-50 break-all">{company.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">Adresse</p>
                    <p className="text-sm font-semibold text-foreground-50">
                      {company.street}, {company.city}
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-8 text-sm text-foreground-400">
                <i className="ri-checkbox-circle-line text-primary-400 mr-2"></i>
                Kostenloses, unverbindliches Angebot · Keine versteckten Kosten
              </p>
            </div>

            <ContactForm formId="kontakt-formular-startseite" />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}